import { memo, useEffect, useMemo, useRef, useState } from 'react'

import { Error, Warning } from '@mui/icons-material'
import { Box, Tooltip, Typography } from '@mui/material'
import { SmepEvent } from '@nativewaves/nw-cloud-sdk/smep'
import { format, formatDistanceToNow, isValid, parseISO } from 'date-fns'
import { useTranslation } from 'react-i18next'

import { RefetchIntervals } from 'types/query'

/**
 * The event session manager rewrites the whole status — and with it the manifest's
 * real-time reference — on every processing loop: `_reprocessInterval` (1 s) in
 * nw-platform's `EventManagerTaskExecution`, backing off to `_errorRetryInterval`
 * (5 s) after a failed iteration. Every one of those loops puts the status into the
 * cache the API serves it from, so this is the interval that matters here; the 2 min
 * `_statusStoreInterval` only governs the S3 fallback copy.
 */
const WORKER_WRITE_INTERVAL = 5_000

/**
 * The oldest a healthy reference can look at the moment we receive it: one worker
 * write interval plus the round trip. A whole frontend refetch cycle on top, so that
 * a single slow loop cannot flip the colour before the next poll gets the chance to
 * disprove it — and so that clock skew between the worker's synced clock and the
 * browser's has somewhere to go.
 */
const DELAYED_AFTER = WORKER_WRITE_INTERVAL + RefetchIntervals.NORMAL

/** Three whole cycles missed — nobody is writing the status anymore. */
const STALLED_AFTER = DELAYED_AFTER * 3

/** Only a running event keeps writing its status — an ended one froze on purpose. */
const MONITORED_STATES: SmepEvent['state'][] = ['Live', 'Preparing']

export type LastStatusUpdateProps = {
  /** `status.realTimeReference` — the worker's clock at the time of its last status write. */
  realTimeReference?: string
  state: SmepEvent['state']
  /** `dataUpdatedAt` of the event query — when this reference was last handed to us. */
  fetchedAt: number
}

const LastStatusUpdateRaw = ({ realTimeReference, state, fetchedAt }: LastStatusUpdateProps) => {
  const { t } = useTranslation(['common', 'domain'])

  // The rendered age has to advance on its own clock: a refetch that brings no change
  // re-renders nothing, which is exactly what a stalled worker produces. The colour
  // does not ride on this tick — it only moves when `fetchedAt` does.
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), RefetchIntervals.NORMAL)

    return () => clearInterval(interval)
  }, [])

  const updatedAt = useMemo(() => {
    const parsed = realTimeReference ? parseISO(realTimeReference) : undefined

    // An event whose status was never written carries .NET's default date instead.
    return parsed && isValid(parsed) && parsed.valueOf() > 0 ? parsed : undefined
  }, [realTimeReference])

  const age = updatedAt ? now - updatedAt.valueOf() : 0

  /**
   * How stale the reference already was when the API handed it over — the age we can
   * actually pin on the worker. Plain `age` also grows whenever *we* stop asking (a
   * backgrounded tab pauses the refetch, and `refetchOnWindowFocus` is off), and our
   * own silence says nothing about the worker, so it must not colour anything.
   */
  const observedAge = updatedAt ? fetchedAt - updatedAt.valueOf() : 0

  /**
   * Whether the response we are looking at carried a reference the previous one did
   * not. Comparing two server-written values needs no clock at all, so it is the one
   * signal a skewed browser cannot fake: a worker that keeps advancing the reference
   * is alive no matter what `observedAge` computes to. First sighting deliberately
   * does not count as an advance — otherwise an event that was already dead when the
   * page opened would look healthy until we happened to catch it standing still.
   */
  const seen = useRef<{ reference?: string; advancedAt?: number }>(undefined)
  if (!seen.current) {
    seen.current = { reference: realTimeReference }
  } else if (seen.current.reference !== realTimeReference) {
    seen.current = { reference: realTimeReference, advancedAt: fetchedAt }
  }
  const advancing = seen.current.advancedAt === fetchedAt

  const severity =
    !updatedAt || !MONITORED_STATES.includes(state) || advancing
      ? undefined
      : observedAge > STALLED_AFTER
        ? 'error'
        : observedAge > DELAYED_AFTER
          ? 'warning'
          : undefined

  return (
    <>
      <Typography sx={{ justifySelf: 'flex-start' }} variant="text">
        {t('domain:Workspace.Event.lastStatusUpdate')}:
      </Typography>
      {updatedAt ? (
        <Tooltip
          title={
            <>
              {format(updatedAt, 'dd.MM.yyyy HH:mm:ss')}
              {!!severity && (
                <Box sx={{ mt: 0.5 }}>
                  {severity === 'error'
                    ? t('domain:Workspace.Event.statusUpdateStalled', { seconds: STALLED_AFTER / 1000 })
                    : t('domain:Workspace.Event.statusUpdateDelayed', { seconds: DELAYED_AFTER / 1000 })}
                </Box>
              )}
            </>
          }
          placement="left"
          arrow
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              columnGap: 0.5,
              minWidth: 0,
              color: severity ? `${severity}.main` : undefined,
            }}
          >
            {/* The colour alone must not be what tells the story. */}
            {severity === 'warning' && <Warning fontSize="inherit" />}
            {severity === 'error' && <Error fontSize="inherit" />}
            <Typography sx={{ color: 'inherit' }} variant="heading-sm" noWrap>
              {/* The worker's clock can run a hair ahead of the browser's — never render "in 3 seconds". */}
              {formatDistanceToNow(age < 0 ? new Date(now) : updatedAt, { addSuffix: true, includeSeconds: true })}
            </Typography>
          </Box>
        </Tooltip>
      ) : (
        <Typography sx={{ color: 'text.disabled' }} variant="heading-sm">
          -
        </Typography>
      )}
    </>
  )
}

export const LastStatusUpdate = memo(LastStatusUpdateRaw)
